import { chunkFile, type FilePart } from './chunkFile';
import { objectStorageService, type CompletedPart } from '../services/objectStorageService';

export interface MultipartProgress {
  fileId: string;
  name: string;
  totalSize: number;
  uploadedBytes: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
  uploadId?: string; // populated after Initiate succeeds; used by abort handlers
}

export interface MultipartOptions {
  projectId: string;
  bucketId: string;
  key: string;
  file: File;
  fileId: string;
  chunkSize?: number;   // default 8 MiB
  concurrency?: number; // default 6
  maxRetries?: number;  // default 3
  onProgress?: (p: MultipartProgress) => void;
  signal?: AbortSignal;
}

const MiB = 1024 * 1024;

/**
 * Sleep with exponential backoff. attempt is 0-indexed (0 = first retry).
 * Caps at 30 s.
 */
function backoff(attempt: number, signal?: AbortSignal): Promise<void> {
  const ms = Math.min(30_000, 1000 * Math.pow(2, attempt));
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(t);
          reject(new Error('aborted'));
        },
        { once: true },
      );
    }
  });
}

/**
 * Run `fn` up to maxRetries+1 times with exponential backoff. Abortable.
 */
async function withRetries<T>(
  fn: () => Promise<T>,
  maxRetries: number,
  signal?: AbortSignal,
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    if (signal?.aborted) throw new Error('aborted');
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt < maxRetries) await backoff(attempt, signal);
    }
  }
  throw lastErr;
}

/**
 * Upload a single file via S3 multipart. Reports progress, supports cancel,
 * retries each part up to maxRetries times with exponential backoff, and
 * always aborts the Wasabi-side multipart on unrecoverable failure to avoid
 * orphan parts.
 */
export async function multipartUpload(opts: MultipartOptions): Promise<void> {
  const chunkSize = opts.chunkSize ?? 8 * MiB;
  const concurrency = opts.concurrency ?? 6;
  const maxRetries = opts.maxRetries ?? 3;

  const emit = (patch: Partial<MultipartProgress>) =>
    opts.onProgress?.({
      fileId: opts.fileId,
      name: opts.file.name,
      totalSize: opts.file.size,
      uploadedBytes: 0,
      status: 'pending',
      ...patch,
    });

  emit({ status: 'pending' });

  let parts: FilePart[];
  try {
    parts = chunkFile(opts.file, chunkSize);
  } catch (err: any) {
    emit({ status: 'error', error: err?.message ?? String(err) });
    throw err;
  }

  const contentType = opts.file.type || 'application/octet-stream';
  let uploadId = '';
  try {
    const init = await objectStorageService.initiateMultipart(
      opts.projectId,
      opts.bucketId,
      opts.key,
      contentType,
    );
    uploadId = init.upload_id;
    emit({ status: 'uploading', uploadId });
  } catch (err: any) {
    emit({ status: 'error', error: err?.response?.data?.error ?? err?.message ?? 'initiate failed' });
    throw err;
  }

  // Track per-part progress so we can sum and emit overall bytes uploaded.
  const partBytes = new Array<number>(parts.length).fill(0);

  // Upload parts with a fixed concurrency. Pool pattern: workers pull from a
  // shared cursor.
  const completed: CompletedPart[] = new Array(parts.length);
  let nextIndex = 0;

  const worker = async () => {
    while (true) {
      const i = nextIndex;
      nextIndex += 1;
      if (i >= parts.length) return;
      if (opts.signal?.aborted) throw new Error('aborted');
      const part = parts[i];
      const result = await withRetries(
        () =>
          objectStorageService.uploadPart(
            opts.projectId,
            opts.bucketId,
            opts.key,
            uploadId,
            part.number,
            part.blob,
            (loaded) => {
              partBytes[i] = loaded;
              const total = partBytes.reduce((a, b) => a + b, 0);
              emit({ status: 'uploading', uploadedBytes: total, uploadId });
            },
            opts.signal,
          ),
        maxRetries,
        opts.signal,
      );
      completed[i] = { part_number: result.part_number, etag: result.etag };
      // Force a final accurate count for this part (in case onProgress missed
      // the final byte event).
      partBytes[i] = part.size;
      const total = partBytes.reduce((a, b) => a + b, 0);
      emit({ status: 'uploading', uploadedBytes: total, uploadId });
    }
  };

  try {
    await Promise.all(Array.from({ length: Math.min(concurrency, parts.length) }, worker));
  } catch (err: any) {
    // Best-effort abort to free Wasabi-side state.
    try {
      await objectStorageService.abortMultipart(opts.projectId, opts.bucketId, opts.key, uploadId);
    } catch {
      /* swallow abort failure */
    }
    const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message ?? '');
    const msg =
      err?.message === 'aborted'
        ? 'cancelled'
        : isTimeout
        ? 'upload part timed out - try a smaller chunk size or check your network'
        : err?.response?.data?.error ?? err?.message ?? 'upload failed';
    emit({ status: 'error', error: msg, uploadId });
    throw err;
  }

  try {
    await objectStorageService.completeMultipart(
      opts.projectId,
      opts.bucketId,
      opts.key,
      uploadId,
      completed,
      opts.file.size,
    );
    emit({ status: 'done', uploadedBytes: opts.file.size, uploadId });
  } catch (err: any) {
    // Backend will have already aborted on quota failure; for non-quota
    // failures attempt a defensive abort too.
    try {
      await objectStorageService.abortMultipart(opts.projectId, opts.bucketId, opts.key, uploadId);
    } catch {
      /* swallow */
    }
    emit({
      status: 'error',
      error: err?.response?.data?.error ?? err?.message ?? 'complete failed',
      uploadId,
    });
    throw err;
  }
}
